import { useMemo } from 'react';
import { Organization } from '../models/organization';

export interface SubscriptionStatusInfo {
  isActive: boolean;
  isPaused: boolean;
  isCancelled: boolean;
  isPendingVes: boolean;
  isVesRejected: boolean;
  isInactive: boolean;
  status: string | undefined;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  disabledReason?: string;
}

export const useSubscriptionStatus = (
  organization: Organization | null
): SubscriptionStatusInfo => {
  return useMemo(() => {
    const subscriptionStatus = organization?.billing?.subscriptionStatus;

    const isActive = subscriptionStatus === 'active';
    const isPaused = subscriptionStatus === 'paused';
    const isCancelled = subscriptionStatus === 'canceled';
    const isPendingVes = subscriptionStatus === 'pending_ves';
    const isVesRejected = subscriptionStatus === 'ves_rejected';
    const isInactive =
      !isActive &&
      (isPaused ||
        isCancelled ||
        isVesRejected ||
        (!subscriptionStatus && !isPendingVes));

    // Define permissions based on subscription status
    let canCreate = true;
    let canEdit = true;
    let canDelete = true;
    let disabledReason: string | undefined;

    if (isVesRejected) {
      canCreate = false;
      canEdit = false;
      canDelete = false;
      disabledReason =
        'Tu pago en Bolívares fue rechazado. Por favor contacta soporte o intenta pagar con tarjeta.';
    } else if (isCancelled) {
      canCreate = false;
      canEdit = false;
      canDelete = false;
      disabledReason =
        'Suscripción cancelada. Reactiva tu suscripción para continuar.';
    } else if (isPaused) {
      canCreate = false;
      canEdit = true; // Allow editing existing data
      canDelete = false;
      disabledReason =
        'Suscripción pausada. Reanuda tu suscripción para crear nuevos elementos.';
    } else if (isPendingVes) {
      // User can use app while VES payment is pending
      canCreate = true;
      canEdit = true;
      canDelete = true;
      disabledReason =
        'Tu pago en Bolívares está en revisión. Puedes usar la app mientras tanto.';
    }

    return {
      isActive,
      isPaused,
      isCancelled,
      isPendingVes,
      isVesRejected,
      isInactive,
      status: subscriptionStatus,
      canCreate,
      canEdit,
      canDelete,
      disabledReason,
    };
  }, [organization?.billing?.subscriptionStatus]);
};
